import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { IPendingEvent, PendingEventCompletion } from '~global-interfaces/IHubAccount';

import { AuthService } from '~features/auth/auth.service';
import { enableSignalWritesInEffectContext } from '~options/effectOptions';
import { UiButtonComponent } from '~ui-store/primitives/button/button.component';
import { UiDateDisplayComponent } from '~ui-store/primitives/date-display/date-display.component';
import { UiDialogComponent } from '~ui-store/primitives/dialog/dialog.component';

import { PendingEventService } from '../pending-event.service';

type AttendanceStatus =
  /** Asking whether they want to confirm. */
  | 'offered'
  | 'confirming'
  /** Their place is reserved — just now, or already before. */
  | 'reserved'
  /** The request failed; confirming again may work. */
  | 'failed'
  /** The event is no longer open to attend; nothing left to confirm. */
  | 'unavailable'
  /** The account was refused (blocked); confirming again won't help. */
  | 'refused';

const STATUS_BY_COMPLETION: Record<PendingEventCompletion, AttendanceStatus> = {
  registered: 'reserved',
  already_registered: 'reserved',
  nothing_pending: 'unavailable',
  signup_required: 'refused',
};

/**
 * The second chance (#686, ADR 0044): someone signed up from an event page,
 * never confirmed their email, and is back and signed in. Their pending event
 * is offered once per session so they can confirm their attendance without
 * confirming the email first.
 *
 * "Register" reads as creating an account, so this dialog never says it.
 */
@Component({
  selector: 'app-pending-event-attendance-dialog',
  imports: [UiDialogComponent, UiButtonComponent, UiDateDisplayComponent],
  templateUrl: './pending-event-attendance-dialog.component.html',
})
export class PendingEventAttendanceDialogComponent {
  private readonly authService = inject(AuthService);
  private readonly pendingEventService = inject(PendingEventService);

  readonly open = signal(false);
  readonly pendingEvent = signal<IPendingEvent | null>(null);
  readonly status = signal<AttendanceStatus>('offered');
  /** Confirm and "No thanks" only make sense while there is still something to confirm. */
  readonly canConfirm = computed(() => ['offered', 'confirming', 'failed'].includes(this.status()));

  /** Asked once per signed-in, unconfirmed session — not again on a token refresh. */
  private offeredThisSession = false;

  constructor() {
    effect(() => {
      const signedInUnconfirmed = this.authService.showConfirmationBanner();
      untracked(() => (signedInUnconfirmed ? this.offer() : this.reset()));
    }, enableSignalWritesInEffectContext);
  }

  confirmAttendance(): void {
    if (this.status() !== 'offered' && this.status() !== 'failed') {
      return;
    }

    this.status.set('confirming');
    this.pendingEventService.completePendingEvent$().subscribe({
      next: (completion) => this.status.set(STATUS_BY_COMPLETION[completion] ?? 'failed'),
      error: () => this.status.set('failed'),
    });
  }

  /** "No thanks": cleared server-side, so it isn't offered again. */
  dismiss(): void {
    this.open.set(false);
    this.pendingEventService.dismissPendingEvent$().subscribe({ error: () => undefined });
  }

  private offer(): void {
    if (this.offeredThisSession) {
      return;
    }
    this.offeredThisSession = true;

    this.pendingEventService.getPendingEvent$().subscribe({
      next: (pendingEvent) => {
        this.status.set('offered');
        this.pendingEvent.set(pendingEvent);
        this.open.set(pendingEvent !== null);
      },
      // A second chance that fails to load is simply not offered.
      error: () => undefined,
    });
  }

  private reset(): void {
    this.offeredThisSession = false;
    this.open.set(false);
    this.pendingEvent.set(null);
    this.status.set('offered');
  }
}
